import React from 'react';
import { observer } from 'mobx-react';
import { UnregisterCallback } from 'history';
import { RouteComponentProps, withRouter } from 'react-router';

import IdleCounterModal, {
    CloseReason,
} from 'components/modals/IdleCounterModal';
import { SdkEvents, ApplicationCloseReason } from 'sdk/index';
import { RoutePaths } from 'helpers/constants/RoutePaths';
import { StoreModel } from 'models/Index';
import { goHome } from 'sdk/InternalSdk';
import { WizardStep } from 'models/kiosk';

interface IdleMonitorProps extends RouteComponentProps<any> {
    /**
     * It's a number in seconds, after which, application will consider that user is away and will show IdleCounterModal
     * @type {number}
     * @memberof IdleMonitorProps
     */
    inactivityTime: number;

    /**
     * It's a number in seconds, which application will show and wait for user activity (countdown time)
     * @type {number}
     * @memberof IdleMonitorProps
     */
    idleWaitTime: number;
    /**
     * It's a root store
     * @type {StoreModel}
     * @memberof IdleMonitorProps
     */
    store: StoreModel;
}

interface IdleMonitorState {
    openWarning: boolean;
}

@observer
class IdleMonitor extends React.Component<IdleMonitorProps, IdleMonitorState> {
    /**
     * Routes where we need to disable inactivity time, since it doesn't make any sense
     */
    routesWithoutTimer = [
        RoutePaths.IdleScreen,
        RoutePaths.KioskOutOfOrder,
        RoutePaths.CashCollection,
        RoutePaths.CashCollectionSummary,
        RoutePaths.CashCollectionSuccess,
    ];

    /**
     * Sdk events which should be considered as user activity
     */
    sdkEvents = [
        SdkEvents.UserActivity,
        SdkEvents.ApplicationOpened,
        SdkEvents.CashAcceptorStarted,
        SdkEvents.PrintReceiptStarted,
    ];

    /**
     * timeoutId of timer which waits specified time to show `Are you still here?` warning
     * it could be null if timer is currently   not set
     * @type {*}
     * @memberof IdleMonitor
     */
    inactivityTimer: any;

    userActivityCallback: any;
    routeUnregisterCallback: UnregisterCallback;
    connectionChangeCallback: any;

    constructor(e: IdleMonitorProps) {
        super(e);

        this.userActivityCallback = this.userActivityListener.bind(this);
        this.connectionChangeCallback =
            this.connectionChangeListener.bind(this);
        this.state = {
            openWarning: false,
        };
    }

    componentDidMount() {
        const { history, store } = this.props;

        //Check current route if we need start timer (actually it's needed only for development)
        this.routerListener(history.location.pathname);

        // Listen router changes for starting/stopping activity monitoring
        this.routeUnregisterCallback = history.listen((location) =>
            this.routerListener(location.pathname)
        );

        //Attach to listen user activity
        document.addEventListener('click', this.userActivityCallback);

        this.sdkEvents.forEach((event) =>
            store.sdk.events.addListener(event, this.userActivityCallback)
        );
        window.addEventListener('offline', this.connectionChangeCallback);
        window.addEventListener('online', this.connectionChangeCallback);
    }

    componentWillUnmount() {
        //Clear user activity listeners
        document.removeEventListener('click', this.userActivityCallback);
        window.removeEventListener('offline', this.connectionChangeCallback);
        window.removeEventListener('online', this.connectionChangeCallback);

        this.sdkEvents.forEach((event) =>
            this.props.store.sdk.events.removeListener(
                event,
                this.userActivityCallback
            )
        );
        this.routeUnregisterCallback();
    }

    render() {
        const { openWarning } = this.state;
        const { idleWaitTime, store } = this.props;

        return (
            <>
                {openWarning && (
                    <IdleCounterModal
                        modalProps={{ open: true }}
                        idleWaitTime={idleWaitTime}
                        onClose={(reason) => {
                            this.setState({ openWarning: false });

                            if (reason === CloseReason.Countdown) {
                                console.debug(
                                    '[InactivityTimer] Redirecting to idle screen...'
                                );

                                store.sdk
                                    .closeApp(ApplicationCloseReason.IdleTimer)
                                    .then(async () => {
                                        if (store.wizard.currentStep === WizardStep.CashAccepter) {
                                            await store.shellClient.cashAcceptor.processReturnCash();
                                            await store.shellClient.cashAcceptor.stopReceivingCash();
    
                                            if (
                                                store.billpay.hasInsertedCash &&
                                                !store.billpay.transactionId
                                            ) {
                                                await store.billpay.completeTransaction();
                                            }
                                        }
                                        
                                    })
                                    //TO BE DISCUSSED: We don't care if application refused to close itself...
                                    .finally(() => {
                                        goHome();
                                        store.billpay.reset();
                                    });
                                return;
                            }

                            this.resetInActivityTimer();
                        }}
                    />
                )}
            </>
        );
    }

    connectionChangeListener(event: any) {
        if (event.type === 'offline') {
            this.stopInActivityTimer();
        }

        if (event.type === 'online') {
            this.startInActivityTimer();
        }
    }

    startInActivityTimer() {
        const { inactivityTime } = this.props;

        //Do not start if it's already started
        if (this.inactivityTimer) {
            return;
        }

        this.inactivityTimer = setTimeout(() => {
            console.debug('[InactivityTimer] Opening warning modal', {
                time: inactivityTime,
                id: this.inactivityTimer,
            });
            this.setState({ openWarning: true });
            this.inactivityTimer = null;
        }, inactivityTime * 1000);

        console.debug('[InactivityTimer] Started', {
            time: inactivityTime,
            id: this.inactivityTimer,
        });
    }

    stopInActivityTimer() {
        //Do not stop if it's already stopped
        if (this.inactivityTimer) {
            console.debug('[InactivityTimer] Stopped', {
                time: this.props.inactivityTime,
                id: this.inactivityTimer,
            });

            clearTimeout(this.inactivityTimer);
            this.inactivityTimer = null;
        }
    }

    resetInActivityTimer() {
        this.stopInActivityTimer();
        this.startInActivityTimer();
    }

    userActivityListener() {
        const { store } = this.props;
        const { openWarning } = this.state;

        // If warning modal already present on screen
        if (openWarning) {
            return;
        }

        //Reset time only if it's already started
        if (this.inactivityTimer) {
            this.resetInActivityTimer();
        }
        store.billpay.onUserActivity();
    }

    routerListener(pathname: string) {
        if (this.routesWithoutTimer.every((route) => pathname !== route)) {
            // start timer to check user inactivity only when we start flow
            this.startInActivityTimer();
        } else {
            // stop timer when we on IdleScreen
            this.setState({ openWarning: false });
            this.stopInActivityTimer();
        }
    }
}

export default withRouter(IdleMonitor);
